import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { MessageSquare, Zap, Users, TrendingUp, DollarSign, RefreshCw, AlertTriangle, PieChart, CreditCard, Activity } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AppHeader from '../components/AppHeader';
import Breadcrumb from '../components/Breadcrumb';
import {
  PageContainer, PageHeader, PageTitle, PageSubtitle,
  Card, CardHeader, CardTitle, Grid, Flex,
  StatCard, StatValue, StatLabel, Badge,
  LoadingContainer, Spinner, Button, SectionTitle
} from '../components/ui';
import { theme } from '../styles/theme';
import { formatCurrency as fmtCur } from '../utils/currency';
import api from '../services/api';

const TIER_PRICES  = { starter: 297, growth: 697, pro: 1497, enterprise: 0 };
const TIER_COLORS  = { starter: theme.colors.gray400, growth: theme.colors.success, pro: theme.colors.primary, enterprise: '#8b5cf6' };
const TIER_LABELS  = { starter: 'Starter', growth: 'Growth', pro: 'Pro', enterprise: 'Enterprise' };
const OVERAGE_RATES = { starter: 0.45, growth: 0.40, pro: 0.35, enterprise: 0 };

const formatBRL = (v) => fmtCur(v, 'BRL', 'pt-BR');

function BarChart({ data, valueKey, labelKey, color, emptyLabel }) {
  if (!data?.length) return <ChartEmpty>{emptyLabel}</ChartEmpty>;
  const max = Math.max(...data.map(d => d[valueKey] || 0), 1);
  const BAR_W = Math.max(8, Math.floor(480 / data.length) - 4);
  const W = data.length * (BAR_W + 4) + 40;
  const H = 120;
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H + 24}`} style={{ overflow: 'visible' }}>
      {data.map((d, i) => {
        const val = d[valueKey] || 0;
        const barH = Math.max(2, Math.round((val / max) * H));
        const x = 20 + i * (BAR_W + 4);
        return (
          <g key={i}>
            <rect x={x} y={H - barH} width={BAR_W} height={barH} fill={color} rx={2} opacity={0.85} />
            <title>{d[labelKey]}: {val}</title>
            {data.length <= 20 && <text x={x + BAR_W / 2} y={H + 14} textAnchor="middle" fontSize={9} fill={theme.colors.gray400}>{String(d[labelKey]).slice(-2)}</text>}
          </g>
        );
      })}
    </svg>
  );
}

function TierPie({ distribution, noTenantsLabel }) {
  const total = Object.values(distribution).reduce((a, b) => a + b, 0);
  if (!total) return <ChartEmpty>{noTenantsLabel}</ChartEmpty>;
  let cumAngle = -Math.PI / 2;
  const R = 60, CX = 80, CY = 70;
  const slices = Object.entries(distribution).map(([tier, count]) => {
    const angle = (count / total) * 2 * Math.PI;
    const x1 = CX + R * Math.cos(cumAngle), y1 = CY + R * Math.sin(cumAngle);
    cumAngle += angle;
    const x2 = CX + R * Math.cos(cumAngle), y2 = CY + R * Math.sin(cumAngle);
    return { tier, count, x1, y1, x2, y2, large: angle > Math.PI ? 1 : 0, pct: Math.round((count / total) * 100) };
  });
  return (
    <PieWrap>
      <svg width={160} height={140}>
        {slices.map(s => (
          <path key={s.tier} d={`M${CX},${CY} L${s.x1},${s.y1} A${R},${R} 0 ${s.large},1 ${s.x2},${s.y2} Z`} fill={TIER_COLORS[s.tier] || theme.colors.gray400} opacity={0.9}>
            <title>{TIER_LABELS[s.tier]}: {s.count} ({s.pct}%)</title>
          </path>
        ))}
      </svg>
      <PieLegend>
        {slices.map(s => (
          <LegendItem key={s.tier}>
            <LegendDot style={{ background: TIER_COLORS[s.tier] }} />
            <span>{TIER_LABELS[s.tier]}</span>
            <LegendCount>{s.count} ({s.pct}%)</LegendCount>
          </LegendItem>
        ))}
      </PieLegend>
    </PieWrap>
  );
}

const SallusAnalytics = () => {
  const { t } = useTranslation('analytics');
  const [month, setMonth]     = useState(() => new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(true);
  const [data, setData]       = useState(null);

  const breadcrumbItems = [
    { label: 'Libben Agents', path: '/sallus/dashboard' },
    { label: t('title') }
  ];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tenantsRes, platformRes] = await Promise.all([
        api.get('/tenants'),
        api.get(`/usage/sallus_platform?month=${month}&breakdown=true`).catch(() => ({ data: {} }))
      ]);
      const tenants      = tenantsRes.data.tenants || [];
      const platformUsage = platformRes.data || {};
      const usages = await Promise.allSettled(
        tenants.map(ten =>
          api.get(`/usage/${ten.tenantId || ten.id}?month=${month}&breakdown=true`)
            .then(r => ({ ...ten, ...r.data }))
            .catch(() => ({ ...ten, messages: 0, conversations: 0, estimatedCostUSD: 0 }))
        )
      );
      const tenantData = usages.filter(r => r.status === 'fulfilled').map(r => r.value);
      const totals = tenantData.reduce((acc, ten) => ({
        messages: acc.messages + (ten.messages || 0),
        conversations: acc.conversations + (ten.conversations || 0),
        tokensInput: acc.tokensInput + (ten.tokensInput || 0),
        tokensOutput: acc.tokensOutput + (ten.tokensOutput || 0),
        toolCalls: acc.toolCalls + (ten.toolCalls || 0),
        costUSD: acc.costUSD + (ten.estimatedCostUSD || 0),
      }), { messages: 0, conversations: 0, tokensInput: 0, tokensOutput: 0, toolCalls: 0, costUSD: 0 });

      const mrrByTier = {}; let mrr = 0, overageRevenue = 0; const tierDist = {};
      for (const ten of tenantData) {
        const tier = ten.tier || 'starter';
        tierDist[tier] = (tierDist[tier] || 0) + 1;
        const price = TIER_PRICES[tier] || 0;
        mrr += price; mrrByTier[tier] = (mrrByTier[tier] || 0) + price;
        overageRevenue += (ten.overageConversations || 0) * (OVERAGE_RATES[tier] || 0);
      }

      const byDayMap = {};
      for (const ten of tenantData) {
        for (const [day, v] of Object.entries(ten.byDay || {})) {
          if (!byDayMap[day]) byDayMap[day] = { day, messages: 0, costUSD: 0 };
          byDayMap[day].messages += v.messages || 0;
          byDayMap[day].costUSD  += v.estimatedCostUSD || 0;
        }
      }
      const byDay = Object.values(byDayMap).sort((a, b) => a.day.localeCompare(b.day));

      // Projeção de custo para o fim do mês
      const now = new Date();
      const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
      const dayOfMonth  = now.getDate();
      const costProjection = dayOfMonth > 0
        ? parseFloat(((totals.costUSD / dayOfMonth) * daysInMonth).toFixed(4))
        : 0;

      const agentCostMap = {};
      for (const ten of tenantData) {
        for (const [agentId, v] of Object.entries(ten.byAgent || {})) {
          if (!agentCostMap[agentId]) agentCostMap[agentId] = { model: agentId, costUSD: 0, messages: 0 };
          agentCostMap[agentId].costUSD  += v.estimatedCostUSD || 0;
          agentCostMap[agentId].messages += v.messages || 0;
        }
      }
      const modelCosts = Object.values(agentCostMap).sort((a, b) => b.costUSD - a.costUSD);

      const churnRisk = tenantData
        .filter(ten => ten.status === 'suspended' || ten.status === 'cancelled' || (ten.messages === 0 && ten.status === 'active'))
        .map(ten => ({ name: ten.name || ten.tenantId, status: ten.status || 'active', messages: ten.messages || 0, tier: ten.tier || 'starter' }));

      // Novos tenants no mês selecionado
      const newThisMonth = tenants.filter(t => t.createdAt?.startsWith(month)).length;
      const prevMonthDate = new Date(month + '-01');
      prevMonthDate.setMonth(prevMonthDate.getMonth() - 1);
      const prevMonth = prevMonthDate.toISOString().slice(0, 7);
      const newLastMonth = tenants.filter(t => t.createdAt?.startsWith(prevMonth)).length;
      const growthMoM = newLastMonth > 0
        ? parseFloat((((newThisMonth - newLastMonth) / newLastMonth) * 100).toFixed(1))
        : null;

      // Cadastros por fonte de aquisição
      const bySource = tenants.reduce((acc, t) => {
        const src = t.acquisitionSource || 'organic';
        acc[src] = (acc[src] || 0) + 1;
        return acc;
      }, {});

      // ARPU por tier (BRL)
      const arpuByTier = {};
      for (const [tier, tierMrr] of Object.entries(mrrByTier)) {
        const count = tierDist[tier] || 0;
        arpuByTier[tier] = count > 0 ? parseFloat((tierMrr / count).toFixed(2)) : 0;
      }

      // Overage como % do MRR
      const overagePctOfMrr = mrr > 0
        ? parseFloat(((overageRevenue / mrr) * 100).toFixed(1))
        : 0;

      const tenantRanking = tenantData
        .map(ten => ({ name: ten.name || ten.tenantId, tier: ten.tier || 'starter', messages: ten.messages || 0, conversations: ten.conversations || 0, tokensInput: ten.tokensInput || 0, tokensOutput: ten.tokensOutput || 0, toolCalls: ten.toolCalls || 0, costUSD: ten.estimatedCostUSD || 0, overageConversations: ten.overageConversations || 0 }))
        .sort((a, b) => b.messages - a.messages);

      setData({ totals, mrr, mrrByTier, overageRevenue, tierDist, byDay, tenantRanking, modelCosts, churnRisk, totalTenants: tenants.length, costProjection, platformUsage, newThisMonth, newLastMonth, growthMoM, bySource, arpuByTier, overagePctOfMrr });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  return (
    <>
      <AppHeader title={t('title')} />
      <BreadcrumbBar><Breadcrumb items={breadcrumbItems} /></BreadcrumbBar>
      <PageContainer>
        <PageHeader>
          <div>
            <PageTitle>{t('title')}</PageTitle>
            <PageSubtitle>{t('subtitle')}</PageSubtitle>
          </div>
          <Flex gap={theme.spacing[2]}>
            <MonthInput type="month" value={month} onChange={e => setMonth(e.target.value)} />
            <Button variant="secondary" size="sm" onClick={load} disabled={loading}>
              <RefreshCw size={14} /> {t('refresh')}
            </Button>
          </Flex>
        </PageHeader>

        {loading ? (
          <LoadingContainer><Spinner size="24px" /> {t('loading')}</LoadingContainer>
        ) : !data ? (
          <LoadingContainer>{t('loadError')}</LoadingContainer>
        ) : (
          <>
            <Grid min="180px" style={{ marginBottom: theme.spacing[6] }}>
              <StatCard accent={theme.colors.success}><Flex gap={theme.spacing[2]}><CreditCard size={16} color={theme.colors.success} /></Flex><StatValue>{formatBRL(data.mrr)}</StatValue><StatLabel>{t('mrr')}</StatLabel></StatCard>
              <StatCard accent={theme.colors.warning}><Flex gap={theme.spacing[2]}><TrendingUp size={16} color={theme.colors.warning} /></Flex><StatValue>{formatBRL(data.overageRevenue)}</StatValue><StatLabel>{t('overageRevenue')}</StatLabel></StatCard>
              <StatCard accent={theme.colors.primary}><Flex gap={theme.spacing[2]}><DollarSign size={16} color={theme.colors.primary} /></Flex><StatValue>{formatBRL(data.mrr + data.overageRevenue)}</StatValue><StatLabel>{t('totalRevenue')}</StatLabel></StatCard>
              <StatCard accent={theme.colors.success}><Flex gap={theme.spacing[2]}><Users size={16} color={theme.colors.success} /></Flex><StatValue>{data.totalTenants}</StatValue><StatLabel>{t('activeTenants')}</StatLabel></StatCard>
              <StatCard accent="#8b5cf6"><Flex gap={theme.spacing[2]}><MessageSquare size={16} color="#8b5cf6" /></Flex><StatValue>{data.totals.messages.toLocaleString('pt-BR')}</StatValue><StatLabel>{t('processedMessages')}</StatLabel></StatCard>
              <StatCard accent={theme.colors.danger}><Flex gap={theme.spacing[2]}><Zap size={16} color={theme.colors.danger} /></Flex><StatValue>$ {data.totals.costUSD.toFixed(2)}</StatValue><StatLabel>{t('awsCost')}</StatLabel></StatCard>
              <StatCard accent="#f97316"><Flex gap={theme.spacing[2]}><TrendingUp size={16} color="#f97316" /></Flex><StatValue>$ {(data.costProjection || 0).toFixed(2)}</StatValue><StatLabel>{t('awsCostProjection')}</StatLabel></StatCard>
            </Grid>

            {/* Aquisição, crescimento e ARPU */}
            <Grid min="180px" style={{ marginBottom: theme.spacing[6] }}>
              <StatCard accent={theme.colors.primary}>
                <StatValue>{data.newThisMonth ?? 0}</StatValue>
                <StatLabel>{t('newTenantsMonth', { defaultValue: 'Novos tenants' })}</StatLabel>
              </StatCard>
              <StatCard accent={data.growthMoM >= 0 ? theme.colors.success : theme.colors.danger}>
                <StatValue>{data.growthMoM !== null ? `${data.growthMoM > 0 ? '+' : ''}${data.growthMoM}%` : '—'}</StatValue>
                <StatLabel>{t('growthMoM', { defaultValue: 'Crescimento MoM' })}</StatLabel>
              </StatCard>
              <StatCard accent="#8b5cf6">
                <StatValue>{data.bySource?.referral ?? 0}</StatValue>
                <StatLabel>{t('sourceReferral', { defaultValue: 'Via indicação' })}</StatLabel>
              </StatCard>
              <StatCard accent={theme.colors.warning}>
                <StatValue>{data.overagePctOfMrr ?? 0}%</StatValue>
                <StatLabel>{t('overagePct', { defaultValue: 'Overage % MRR' })}</StatLabel>
              </StatCard>
              {Object.entries(data.arpuByTier || {}).map(([tier, arpu]) => (
                <StatCard key={tier} accent={TIER_COLORS[tier] || theme.colors.textMuted}>
                  <StatValue>{formatBRL(arpu)}</StatValue>
                  <StatLabel>ARPU {TIER_LABELS[tier] || tier}</StatLabel>
                </StatCard>
              ))}
            </Grid>

            {data.platformUsage?.tokensInput > 0 || data.platformUsage?.helpChatMessages > 0 || data.platformUsage?.helpChatActionMessages > 0 ? (
              <Card style={{ marginBottom: theme.spacing[6] }}>
                <CardHeader>
                  <Flex gap={theme.spacing[2]}>
                    <Activity size={18} color="#8b5cf6" />
                    <CardTitle>{t('platformCostTitle')}</CardTitle>
                  </Flex>
                  <Badge variant="neutral">{t('platformCostBadge')}</Badge>
                </CardHeader>
                <Grid min="160px">
                  <StatCard accent="#8b5cf6">
                    <StatValue>{(data.platformUsage.helpChatMessages || 0).toLocaleString('pt-BR')}</StatValue>
                    <StatLabel>{t('platformHelpChats')}</StatLabel>
                  </StatCard>
                  <StatCard accent="${theme.colors.purple600}">
                    <StatValue>{(data.platformUsage.helpChatActionMessages || 0).toLocaleString('pt-BR')}</StatValue>
                    <StatLabel>{t('platformActionChats', { defaultValue: 'Chats com Ações' })}</StatLabel>
                  </StatCard>
                  <StatCard accent={theme.colors.primary}>
                    <StatValue>{((data.platformUsage.tokensInput || 0) + (data.platformUsage.tokensOutput || 0)).toLocaleString('pt-BR')}</StatValue>
                    <StatLabel>{t('platformTokens')}</StatLabel>
                  </StatCard>
                  <StatCard accent={theme.colors.danger}>
                    <StatValue>$ {(data.platformUsage.estimatedCostUSD || 0).toFixed(4)}</StatValue>
                    <StatLabel>{t('platformCostUSD')}</StatLabel>
                  </StatCard>
                </Grid>
              </Card>
            ) : null}

            <Grid min="300px" style={{ marginBottom: theme.spacing[6] }}>
              <Card>
                <CardHeader><Flex gap={theme.spacing[2]}><PieChart size={18} color={theme.colors.primary} /><CardTitle>{t('tierDistribution')}</CardTitle></Flex></CardHeader>
                <TierPie distribution={data.tierDist} noTenantsLabel={t('noDataChart')} />
              </Card>
              <Card>
                <CardHeader><Flex gap={theme.spacing[2]}><CreditCard size={18} color={theme.colors.primary} /><CardTitle>{t('mrrByTier')}</CardTitle></Flex></CardHeader>
                <MRRList>
                  {Object.entries(data.mrrByTier).sort((a, b) => b[1] - a[1]).map(([tier, val]) => (
                    <MRRItem key={tier}>
                      <Flex gap={theme.spacing[2]}>
                        <TierDot style={{ background: TIER_COLORS[tier] }} />
                        <span>{TIER_LABELS[tier] || tier}</span>
                        <Badge variant="neutral">{data.tierDist[tier] || 0} {t('tenants')}</Badge>
                      </Flex>
                      <MRRValue>{formatBRL(val)}</MRRValue>
                    </MRRItem>
                  ))}
                  <MRRTotal><span>{t('totalMrr')}</span><span>{formatBRL(data.mrr)}</span></MRRTotal>
                </MRRList>
              </Card>
            </Grid>

            <Grid min="380px" style={{ marginBottom: theme.spacing[6] }}>
              <Card><SectionTitle>{t('messagesByDay', { month })}</SectionTitle><BarChart data={data.byDay} valueKey="messages" labelKey="day" color={theme.colors.primary} emptyLabel={t('noDataChart')} /></Card>
              <Card><SectionTitle>{t('costByDay', { month })}</SectionTitle><BarChart data={data.byDay} valueKey="costUSD" labelKey="day" color={theme.colors.danger} emptyLabel={t('noDataChart')} /></Card>
            </Grid>

            {data.modelCosts.length > 0 && (
              <Card style={{ marginBottom: theme.spacing[6] }}>
                <CardHeader><Flex gap={theme.spacing[2]}><Activity size={18} color={theme.colors.primary} /><CardTitle>{t('costByModel', { month })}</CardTitle></Flex></CardHeader>
                <TableScroll><ModelTable>
                  <thead><tr><Th>{t('colModel')}</Th><Th align="right">{t('colMessages')}</Th><Th align="right">{t('colCost')}</Th><Th align="right">{t('colPercent')}</Th></tr></thead>
                  <tbody>
                    {data.modelCosts.map(m => (
                      <tr key={m.model}>
                        <Td><ModelName>{m.model}</ModelName></Td>
                        <Td align="right">{m.messages.toLocaleString('pt-BR')}</Td>
                        <Td align="right"><Cost>$ {m.costUSD.toFixed(4)}</Cost></Td>
                        <Td align="right">{data.totals.costUSD > 0 ? `${Math.round((m.costUSD / data.totals.costUSD) * 100)}%` : '—'}</Td>
                      </tr>
                    ))}
                  </tbody>
                </ModelTable></TableScroll>
              </Card>
            )}

            {data.churnRisk.length > 0 && (
              <Card style={{ marginBottom: theme.spacing[6] }}>
                <CardHeader>
                  <Flex gap={theme.spacing[2]}><AlertTriangle size={18} color={theme.colors.warning} /><CardTitle>{t('churnRisk')}</CardTitle></Flex>
                  <Badge variant="warning">{data.churnRisk.length} {t('tenants')}</Badge>
                </CardHeader>
                <ChurnList>
                  {data.churnRisk.map(ten => (
                    <ChurnItem key={ten.name}>
                      <Flex gap={theme.spacing[2]}>
                        <TierDot style={{ background: TIER_COLORS[ten.tier] }} />
                        <ChurnName>{ten.name}</ChurnName>
                        <Badge variant={ten.status === 'active' ? 'warning' : 'danger'}>
                          {ten.status === 'active' ? t('noUsage') : ten.status}
                        </Badge>
                      </Flex>
                      <ChurnTier>{TIER_LABELS[ten.tier]}</ChurnTier>
                    </ChurnItem>
                  ))}
                </ChurnList>
              </Card>
            )}

            <Card>
              <SectionTitle>{t('tenantRanking', { month })}</SectionTitle>
              {data.tenantRanking.length === 0 ? (
                <Empty>{t('noData')}</Empty>
              ) : (
                <TableScroll><TenantTable>
                  <thead><tr><Th>{t('colRank')}</Th><Th>{t('colTenant')}</Th><Th>{t('colTier')}</Th><Th align="right">{t('colConversations')}</Th><Th align="right">{t('colMessages')}</Th><Th align="right">{t('colOverage')}</Th><Th align="right">{t('colCost')}</Th></tr></thead>
                  <tbody>
                    {data.tenantRanking.map((ten, i) => (
                      <tr key={ten.name}>
                        <Td><Rank>{i + 1}</Rank></Td>
                        <Td><TenantName>{ten.name}</TenantName></Td>
                        <Td><Badge variant={ten.tier === 'pro' ? 'primary' : ten.tier === 'growth' ? 'success' : 'neutral'}>{TIER_LABELS[ten.tier] || ten.tier}</Badge></Td>
                        <Td align="right">{ten.conversations.toLocaleString('pt-BR')}</Td>
                        <Td align="right">{ten.messages.toLocaleString('pt-BR')}</Td>
                        <Td align="right">{ten.overageConversations > 0 ? <OverageVal>+{ten.overageConversations}</OverageVal> : '—'}</Td>
                        <Td align="right"><Cost>$ {ten.costUSD.toFixed(4)}</Cost></Td>
                      </tr>
                    ))}
                  </tbody>
                </TenantTable></TableScroll>
              )}
            </Card>
          </>
        )}
      </PageContainer>
    </>
  );
};

const BreadcrumbBar = styled.div`background: ${theme.colors.white}; padding: 0 ${theme.spacing[6]}; border-bottom: 1px solid ${theme.colors.border};`;
// SectionTitle — importado de ui.js
const MonthInput = styled.input`padding: ${theme.spacing[1]} ${theme.spacing[2]}; border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.md}; font-size: ${theme.typography.sm}; font-family: inherit; &:focus { outline: none; border-color: ${theme.colors.primary}; }`;
const ChartEmpty = styled.div`height: 120px; display: flex; align-items: center; justify-content: center; color: ${theme.colors.textMuted}; font-size: ${theme.typography.sm};`;
const Empty = styled.div`padding: ${theme.spacing[8]}; text-align: center; color: ${theme.colors.textMuted}; font-size: ${theme.typography.sm};`;
const PieWrap = styled.div`display: flex; align-items: center; gap: ${theme.spacing[4]}; flex-wrap: wrap;`;
const PieLegend = styled.div`display: flex; flex-direction: column; gap: ${theme.spacing[2]};`;
const LegendItem = styled.div`display: flex; align-items: center; gap: ${theme.spacing[2]}; font-size: ${theme.typography.sm}; color: ${theme.colors.textSecondary};`;
const LegendDot = styled.div`width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0;`;
const LegendCount = styled.span`color: ${theme.colors.textMuted}; font-size: ${theme.typography.xs};`;
const MRRList = styled.div`display: flex; flex-direction: column; gap: ${theme.spacing[3]}; margin-top: ${theme.spacing[2]};`;
const MRRItem = styled.div`display: flex; justify-content: space-between; align-items: center; font-size: ${theme.typography.sm};`;
const MRRValue = styled.span`font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary};`;
const MRRTotal = styled.div`display: flex; justify-content: space-between; font-size: ${theme.typography.base}; font-weight: ${theme.typography.bold}; color: ${theme.colors.textPrimary}; border-top: 1px solid ${theme.colors.border}; padding-top: ${theme.spacing[2]}; margin-top: ${theme.spacing[1]};`;
const TierDot = styled.div`width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0;`;
const TableScroll = styled.div`overflow-x: auto; -webkit-overflow-scrolling: touch;`;
const ModelTable = styled.table`width: 100%; min-width: 480px; border-collapse: collapse; font-size: ${theme.typography.sm}; margin-top: ${theme.spacing[2]};`;
const ModelName = styled.span`font-family: monospace; font-size: ${theme.typography.xs}; color: ${theme.colors.textSecondary};`;
const ChurnList = styled.div`display: flex; flex-direction: column; gap: ${theme.spacing[2]}; margin-top: ${theme.spacing[2]};`;
const ChurnItem = styled.div`display: flex; justify-content: space-between; align-items: center; padding: ${theme.spacing[2]} 0; border-bottom: 1px solid ${theme.colors.gray100}; &:last-child { border-bottom: none; }`;
const ChurnName = styled.span`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.medium}; color: ${theme.colors.textPrimary};`;
const ChurnTier = styled.span`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted};`;
const TenantTable = styled.table`width: 100%; min-width: 700px; border-collapse: collapse; font-size: ${theme.typography.sm};`;
const Th = styled.th`padding: ${theme.spacing[2]} ${theme.spacing[3]}; text-align: ${p => p.align || 'left'}; color: ${theme.colors.textMuted}; font-weight: ${theme.typography.medium}; border-bottom: 1px solid ${theme.colors.border};`;
const Td = styled.td`padding: ${theme.spacing[2]} ${theme.spacing[3]}; text-align: ${p => p.align || 'left'}; border-bottom: 1px solid ${theme.colors.gray100};`;
const Rank = styled.span`color: ${theme.colors.textMuted}; font-weight: ${theme.typography.medium};`;
const TenantName = styled.span`font-weight: ${theme.typography.medium}; color: ${theme.colors.textPrimary};`;
const Cost = styled.span`color: ${theme.colors.danger}; font-weight: ${theme.typography.semibold};`;
const OverageVal = styled.span`color: ${theme.colors.warning}; font-weight: ${theme.typography.semibold};`;

export default SallusAnalytics;
